import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaUser, FaArrowLeft, FaEye, FaStar } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { setPageOgMeta } from '../utils/ogMeta';
import { error as logError } from '../utils/logger';
import MoreNewsCarousel from './MoreNewsCarousel';
import '../styles/Blog.css';
import SiteHeader from './SiteHeader';
import './Landing.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const featuredImageUrl = (url) =>
  !url ? '' : (url.startsWith('http') ? url : API_BASE + url);

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const currentLanguage = language || 'es';
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const viewCountedRef = useRef(false);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    fetch(`${API_BASE}/auth/me`, { credentials: 'include' })
      .then((r) => { if (r.ok) setIsLoggedIn(true); })
      .catch(() => {});
  }, [post]);

  const loadPost = async () => {
    if (!slug) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/api/blog/posts/slug/${encodeURIComponent(slug)}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(t('common.blog.postNotFound', currentLanguage));
        } else {
          setError(t('common.blog.errorLoading', currentLanguage));
        }
        return;
      }
      
      const data = await response.json();
      setPost(data);
      const pageUrl = window.location.origin + window.location.pathname;
      const image = featuredImageUrl(data.featured_image);
      setPageOgMeta({
        title: data.meta_title || data.title,
        description: data.meta_description || data.excerpt || data.title,
        image: image || undefined,
        url: pageUrl,
        type: 'article'
      });

      // Registrar vista una sola vez por carga de página
      if (!viewCountedRef.current) {
        viewCountedRef.current = true;
        fetch(`${API_BASE}/api/blog/posts/slug/${encodeURIComponent(slug)}/view`, {
          method: 'POST',
          credentials: 'include'
        }).then(() => {
          if (data.views !== undefined) setPost((p) => p ? { ...p, views: (p.views || 0) + 1 } : p);
        }).catch(() => {});
      }
    } catch (err) {
      logError('Error cargando post:', err);
      setError(t('common.blog.errorLoading', currentLanguage));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      business: currentLanguage === 'en' ? 'Business' : 'Negocios',
      tech: currentLanguage === 'en' ? 'Tech' : 'Tecnología',
      news: currentLanguage === 'en' ? 'News' : 'Actualidad',
      movie: currentLanguage === 'en' ? 'Business' : 'Negocios',
      series: currentLanguage === 'en' ? 'Tech' : 'Tecnología',
      music: currentLanguage === 'en' ? 'News' : 'Actualidad'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      business: 'primary',
      tech: 'success',
      news: 'warning',
      movie: 'primary',
      series: 'success',
      music: 'warning'
    };
    return colors[category] || 'secondary';
  };

  const getRateLinkLabel = (category) => {
    if (category === 'business' || category === 'movie') return t('common.blog.rateBusiness', currentLanguage);
    if (category === 'tech' || category === 'series') return t('common.blog.rateTech', currentLanguage);
    if (category === 'news' || category === 'music') return t('common.blog.rateNews', currentLanguage);
    return t('common.blog.rateThisContent', currentLanguage);
  };

  const getRateLinkUrl = () => {
    const params = new URLSearchParams();
    if (post?.category) params.set('category', post.category);
    const searchTerm = (post?.related_title || post?.title || '').trim();
    if (searchTerm) params.set('search', searchTerm);
    const q = params.toString();
    return `/app${q ? `?${q}` : ''}#recommendations`;
  };

  if (loading) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="marketing" />
        <Container className="site-main-with-header py-5 text-secondary">
          <div className="text-center">
            <Spinner animation="border" variant="light" />
            <p className="mt-3">{t('common.blog.loading', currentLanguage)}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="marketing" />
        <Container className="site-main-with-header py-5 text-secondary">
          <Alert variant="danger">
            <Alert.Heading>{t('common.blog.error', currentLanguage)}</Alert.Heading>
            <p>{error || t('common.blog.postNotFound', currentLanguage)}</p>
            <button type="button" className="btn btn-outline-light" onClick={() => navigate('/')}>
              <FaArrowLeft className="me-2" />
              {t('common.blog.backToHome', currentLanguage)}
            </button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="blog-post-page landing-theme-dark">
      <SiteHeader variant="marketing" />
      <header className="blog-post-header site-main-with-header">
        <Container>
          <button type="button" className="blog-post-back" onClick={() => navigate('/')}>
            <FaArrowLeft className="me-2" />
            {t('common.blog.backToHome', currentLanguage)}
          </button>
        </Container>
      </header>

      <article className="blog-article">
        {/* Hero con imagen destacada */}
        {post.featured_image && (
          <div className="blog-hero-image">
            <img src={featuredImageUrl(post.featured_image)} alt={post.title} />
            <div className="blog-hero-overlay" />
          </div>
        )}

        <Container className="blog-article-container">
          <div className="blog-article-meta">
            <Badge bg={getCategoryColor(post.category)} className="blog-category-badge">
              {getCategoryLabel(post.category)}
            </Badge>
            <h1 className="blog-article-title">{post.title}</h1>
            <div className="blog-article-byline">
              {post.author_name && (
                <span className="blog-byline-item">
                  <FaUser className="me-1" />
                  {post.author_name}
                </span>
              )}
              {post.published_at && (
                <span className="blog-byline-item">
                  <FaCalendarAlt className="me-1" />
                  {formatDate(post.published_at)}
                </span>
              )}
              {post.views > 0 && (
                <span className="blog-byline-item">
                  <FaEye className="me-1" />
                  {post.views} {t('common.blog.views', currentLanguage)}
                </span>
              )}
            </div>
            {post.excerpt && (
              <p className="blog-article-lead">{post.excerpt}</p>
            )}
            {isLoggedIn && (
              <div className="blog-rate-cta mt-3">
                <Link to={getRateLinkUrl()} className="btn btn-outline-primary btn-sm">
                  <FaStar className="me-2" />
                  {getRateLinkLabel(post.category)}
                </Link>
              </div>
            )}
          </div>

          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Container>
      </article>

      <MoreNewsCarousel excludePostId={post.id} limit={10} />
    </div>
  );
};

export default BlogPost;
